// DEPRECATED: Prefer using the useAssessmentQuery() hook from app/src/hooks/useAssessment.ts directly.
// This context is kept for backward compatibility.

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { questions, categories, getQuestionsByCategory } from "@/data/transformationQuestions";
import { assessmentService } from "@/services/assessmentService";

export interface AssessmentResponses {
  [questionId: string]: number;
}

export interface CategoryScore {
  categoryId: string;
  name: string;
  score: number;
  weight: number;
  questionsAnswered: number;
  totalQuestions: number;
}

export interface AssessmentState {
  responses: AssessmentResponses;
  lastCompletedAt: string | null;
  isComplete: boolean;
}

interface AssessmentContextType {
  responses: AssessmentResponses;
  setResponse: (questionId: string, value: number) => void;
  clearResponses: () => void;
  getCategoryScores: () => CategoryScore[];
  getOverallQuestionnaireScore: () => number;
  getCombinedScore: (autoCalculatedScore: number) => number;
  isComplete: boolean;
  lastCompletedAt: string | null;
  completeAssessment: () => void;
  getProgress: () => { answered: number; total: number; percentage: number };
}

const AssessmentContext = createContext<AssessmentContextType | undefined>(undefined);

export function AssessmentProvider({ children }: { children: ReactNode }) {
  const { hasTenant } = useAuth() as { hasTenant: boolean };

  const [state, setState] = useState<AssessmentState>({
    responses: {},
    lastCompletedAt: null,
    isComplete: false,
  });

  // Load saved state from API on mount
  const { data: savedState } = useQuery({
    queryKey: ["assessment"],
    queryFn: assessmentService.get,
    enabled: hasTenant,
  });

  useEffect(() => {
    if (savedState) {
      setState(savedState);
    }
  }, [savedState]);

  const saveMutation = useMutation({
    mutationFn: (s: AssessmentState) =>
      assessmentService.upsert({ responses: s.responses, is_complete: s.isComplete }),
  });

  const clearMutation = useMutation({
    mutationFn: assessmentService.clear,
    onSuccess: () =>
      setState({ responses: {}, lastCompletedAt: null, isComplete: false }),
  });

  // Debounced auto-save whenever responses change
  useEffect(() => {
    if (!hasTenant || Object.keys(state.responses).length === 0) return;
    const timer = setTimeout(() => saveMutation.mutate(state), 1000);
    return () => clearTimeout(timer);

  }, [state.responses, state.isComplete]);

  const setResponse = (questionId: string, value: number) => {
    setState((prev) => ({
      ...prev,
      responses: { ...prev.responses, [questionId]: value },
      isComplete: false,
    }));
  };

  const clearResponses = () => {
    if (hasTenant) {
      clearMutation.mutate();
    } else {
      setState({ responses: {}, lastCompletedAt: null, isComplete: false });
    }
  };

  const getCategoryScores = (): CategoryScore[] => {
    return categories.map((category) => {
      const categoryQuestions = getQuestionsByCategory(category.id);
      const answeredQuestions = categoryQuestions.filter(
        (q) => state.responses[q.id] !== undefined
      );

      if (answeredQuestions.length === 0) {
        return {
          categoryId: category.id,
          name: category.name,
          score: 0,
          weight: category.weight,
          questionsAnswered: 0,
          totalQuestions: categoryQuestions.length,
        };
      }

      const totalScore = answeredQuestions.reduce(
        (sum, q) => sum + (state.responses[q.id] || 0),
        0
      );
      const avgScore = totalScore / answeredQuestions.length;
      const normalizedScore = ((avgScore - 1) / 4) * 100;

      return {
        categoryId: category.id,
        name: category.name,
        score: Math.round(normalizedScore),
        weight: category.weight,
        questionsAnswered: answeredQuestions.length,
        totalQuestions: categoryQuestions.length,
      };
    });
  };

  const getOverallQuestionnaireScore = (): number => {
    const categoryScores = getCategoryScores();
    const totalAnswered = categoryScores.reduce((sum, cat) => sum + cat.questionsAnswered, 0);
    if (totalAnswered === 0) return 0;
    const weightedSum = categoryScores.reduce(
      (sum, cat) => sum + cat.score * (cat.weight / 100),
      0
    );
    return Math.round(weightedSum);
  };

  const getCombinedScore = (autoCalculatedScore: number): number => {
    const questionnaireScore = getOverallQuestionnaireScore();
    const hasResponses = Object.keys(state.responses).length > 0;
    if (!hasResponses) return autoCalculatedScore;
    return Math.round(autoCalculatedScore * 0.4 + questionnaireScore * 0.6);
  };

  const getProgress = () => {
    const totalQuestions = questions.length;
    const answeredQuestions = Object.keys(state.responses).length;
    return {
      answered: answeredQuestions,
      total: totalQuestions,
      percentage: Math.round((answeredQuestions / totalQuestions) * 100),
    };
  };

  const completeAssessment = () => {
    const newState: AssessmentState = {
      ...state,
      lastCompletedAt: new Date().toISOString(),
      isComplete: true,
    };
    setState(newState);
    if (hasTenant) {
      saveMutation.mutate(newState);
    }
  };

  return (
    <AssessmentContext.Provider
      value={{
        responses: state.responses,
        setResponse,
        clearResponses,
        getCategoryScores,
        getOverallQuestionnaireScore,
        getCombinedScore,
        isComplete: state.isComplete,
        lastCompletedAt: state.lastCompletedAt,
        completeAssessment,
        getProgress,
      }}
    >
      {children}
    </AssessmentContext.Provider>
  );
}

export function useAssessment() {
  const context = useContext(AssessmentContext);
  if (context === undefined) {
    throw new Error("useAssessment must be used within an AssessmentProvider");
  }
  return context;
}
